import { useState, useEffect } from 'react';
import { Search, Mic, MicOff, SlidersHorizontal, X } from 'lucide-react';
import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition';
import type { Category, SubCategory } from '../../../types/category.type';

export interface SearchResult {
  category: Category;
  subCategory: SubCategory;
}

interface SearchFilterBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filters: {
    mode: string;
    categoryType: string;
    specialization: string;
  };
  setFilters: (filters: any) => void;
  categories: Category[];
  onSearchResults?: (results: SearchResult[], isSearching: boolean) => void;
}

export function SearchFilterBar({ 
  searchQuery, 
  setSearchQuery, 
  filters, 
  setFilters,
  categories,
  onSearchResults
}: SearchFilterBarProps) {
  const [isSticky, setIsSticky] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Use speech recognition hook
  const { isListening, startListening, stopListening } = useSpeechRecognition({
    onResult: (transcript) => setSearchQuery(transcript),
    onError: (error) => console.error('Speech recognition error:', error)
  });

  // Handle sticky header on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-search when query or filters change
  useEffect(() => {
    if (searchQuery.trim() || filters.categoryType !== 'all' || filters.specialization !== 'all') {
      handleSearch();
    } else {
      onSearchResults?.([], false);
    }
  }, [searchQuery, filters]);

  const handleSearch = () => {
    const results: SearchResult[] = [];

    categories.forEach(category => {
      // Filter by category type first
      if (filters.categoryType !== 'all' && category.name !== filters.categoryType) {
        return;
      }

      category.subCategories.forEach(subCategory => {
        let matches = true;

        // Filter by search query
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          matches = 
            subCategory.name.toLowerCase().includes(query) ||
            subCategory.description?.toLowerCase().includes(query) ||
            category.name.toLowerCase().includes(query);
        }

        // Filter by specialization
        if (filters.specialization !== 'all') {
          matches = matches && subCategory.name === filters.specialization;
        }

        if (matches) {
          results.push({ category, subCategory });
        }
      });
    });

    onSearchResults?.(results, true);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setFilters({
      mode: "all",
      categoryType: "all",
      specialization: "all",
    });
    onSearchResults?.([], false);
  };

  const handleVoiceSearch = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Get unique category names for filter dropdown
  const categoryNames = Array.from(new Set(categories.map(cat => cat.name)));

  // Get subcategories based on selected category
  const getFilteredSubCategories = () => {
    if (filters.categoryType === 'all') {
      const allSubCategories = categories.flatMap(cat => 
        cat.subCategories.map(sub => ({ ...sub, categoryName: cat.name }))
      );
      return Array.from(
        new Map(allSubCategories.map(sub => [sub.id, sub])).values()
      );
    } else {
      const selectedCategory = categories.find(cat => cat.name === filters.categoryType);
      return selectedCategory?.subCategories || [];
    }
  };

  const filteredSubCategories = getFilteredSubCategories();

  return (
    <>
      <style>{`
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(236, 72, 153, 0.6), 0 0 40px rgba(168, 85, 247, 0.4);
          }
          50% {
            box-shadow: 0 0 30px rgba(236, 72, 153, 0.8), 0 0 60px rgba(168, 85, 247, 0.6);
          }
        }
      `}</style>
      
      {/* 🟢 FIXED: Changed 'top-0' to 'top-16' (64px) and 'z-50' to 'z-40' so it sits perfectly under your Navbar */}
      <div className={`bg-white border-b border-gray-200 transition-all duration-300 ${
        isSticky ? 'fixed top-16 left-0 right-0 z-40 shadow-md' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex gap-2 sm:gap-4 items-center">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder={isListening ? "Listening..." : "Search categories or experts…"}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button 
                  onClick={handleClearSearch}
                  className="absolute right-10 sm:right-12 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                </button>
              )}
              <button 
                onClick={handleVoiceSearch}
                className={`absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 rounded-full transition-all ${
                  isListening 
                    ? 'p-1.5 bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 shadow-lg shadow-pink-500/50' 
                    : 'p-1 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 hover:from-pink-500 hover:via-purple-500 hover:to-blue-500 shadow-md shadow-purple-300/40'
                }`}
                title={isListening ? "Stop listening" : "Voice search"}
                style={isListening ? {
                  animation: 'pulse 1.5s ease-in-out infinite, glow 2s ease-in-out infinite'
                } : {}}
              >
                {isListening ? (
                  <MicOff className="w-4 h-4 sm:w-5 sm:h-5 text-white animate-pulse" />
                ) : (
                  <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                )}
              </button>
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-full border transition-all flex-shrink-0 ${
                showFilters 
                  ? 'bg-blue-600 text-white border-blue-600' 
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500 hover:text-blue-600'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Filters</span>
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="pt-4 sm:pt-6 pb-2 flex flex-col gap-3 sm:gap-4 md:grid md:grid-cols-3">
              {/* Category Type */}
              <div>
                <label className="block text-xs sm:text-sm text-gray-700 mb-1.5 sm:mb-2">Category</label>
                <select
                  value={filters.categoryType}
                  onChange={(e) => setFilters({ ...filters, categoryType: e.target.value, specialization: 'all' })}
                  className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white max-h-48 overflow-y-auto"
                  style={{ appearance: 'none', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")', backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                >
                  <option value="all">All Categories</option>
                  {categoryNames.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              {/* Specialization */}
              <div>
                <label className="block text-xs sm:text-sm text-gray-700 mb-1.5 sm:mb-2">Specialization</label>
                <select
                  value={filters.specialization}
                  onChange={(e) => setFilters({ ...filters, specialization: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white max-h-48 overflow-y-auto disabled:bg-gray-50 disabled:text-gray-500"
                  disabled={filteredSubCategories.length === 0}
                  style={{ appearance: 'none', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")', backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                >
                  <option value="all">All Specializations</option>
                  {filteredSubCategories.map(sub => (
                    <option key={sub.id} value={sub.name}>{sub.name}</option>
                  ))}
                </select>
              </div>

              {/* Clear Filters Button */}
              <div className="md:flex md:items-end">
                <button
                  onClick={handleClearSearch}
                  className="w-full px-4 sm:px-6 py-2 text-sm sm:text-base bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Spacer when sticky */}
      {isSticky && <div className="h-16 sm:h-20"></div>}
    </>
  );
}